import { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import ParametersActions from "../../../redux/reducers/parameters.reducer";
import App from "../../../lib/app";

/**
 * Hook para acceder a parámetros del sistema desde Redux store
 * Si los parámetros no están cargados, los solicita automáticamente
 * @returns {Object} { parameters, isLoading, getNumericValue, getStringValue }
 */
export const useSystemParameters = () => {
  const dispatch = useDispatch();
  
  // Obtener parámetros desde Redux store
  const { parameters: parametersList, isLoading } = useSelector(
    ({ parameters }) => parameters
  );

  // Cargar parámetros si no están disponibles
  useEffect(() => {
    if (!parametersList || parametersList.length === 0) {
      dispatch(ParametersActions.getAllParameters());
    }
  }, [dispatch, parametersList]);

  // Convertir array de parámetros a objeto key-value para fácil acceso
  const parameters = useMemo(() => {
    if (!parametersList || parametersList.length === 0) {
      // Fallback: si el loader global ya cargó parámetros en `App.parameters`, úsalos
      if (App && App.parameters) {
        return App.parameters;
      }
      return {};
    }
    
    return parametersList.reduce((acc, param) => {
      acc[param.name] = {
        value: param.value,
        description: param.description,
        scope: param.scope,
        json_value: param.json_value
      };
      return acc;
    }, {});
  }, [parametersList]);

  /**
   * Obtiene el valor numérico de un parámetro
   * @param {string} paramName - Nombre del parámetro
   * @param {number} defaultValue - Valor por defecto si no existe
   * @returns {number}
   */
  const getNumericValue = (paramName, defaultValue = 0) => {
    const param = parameters[paramName];
    if (!param || !param.value) {
      return defaultValue;
    }
    return parseFloat(param.value) || defaultValue;
  };

  /**
   * Obtiene el valor string de un parámetro
   * @param {string} paramName - Nombre del parámetro
   * @param {string} defaultValue - Valor por defecto si no existe
   * @returns {string}
   */
  const getStringValue = (paramName, defaultValue = "") => {
    const param = parameters[paramName];
    if (!param || !param.value) {
      return defaultValue;
    }
    return param.value;
  };

  return {
    parameters,
    isLoading,
    getNumericValue,
    getStringValue
  };
};

